<template>
  <div class="space-y-3">
    <div v-if="title" class="text-md font-semibold text-gray-800 mb-2">{{ title }}</div>
    <div v-if="popularityData && Object.keys(popularityData).length > 0">
      <!-- Popular Times (Histogram-like data) -->
      <div v-if="popularityData.popular_times || popularityData.histogram" class="mb-4 p-3 bg-gray-50 rounded-md shadow-sm">
        <h4 class="text-sm font-medium text-gray-700 mb-2">Popular Times</h4>
        <div v-if="(popularityData.popular_times || popularityData.histogram).length > 0">
          <div v-for="(dayData, dayIndex) in (popularityData.popular_times || popularityData.histogram)" :key="dayIndex" class="mb-2">
            <p class="text-xs font-semibold text-gray-600">{{ getDayName(dayData.day || dayIndex) }}</p>
            <div class="flex space-x-1 mt-1" v-if="Array.isArray(dayData.values || dayData.data)">
              <div 
                v-for="(value, hourIndex) in (dayData.values || dayData.data)" 
                :key="hourIndex" 
                class="h-4 bg-primary rounded-sm tooltip-container"
                :style="{ width: (value / 100 * 20) + 'px' }" 
              >
                <span class="tooltip-text">{{ getHourLabel(hourIndex) }}: {{ value }}% busy</span>
              </div>
            </div>
            <p v-else class="text-xs text-gray-500 italic">No hourly data</p>
          </div>
        </div>
        <p v-else class="text-xs text-gray-500 italic">No popular times data.</p>
      </div>

      <!-- Peak Hours -->
      <div v-if="popularityData.peak_hours" class="p-3 bg-gray-50 rounded-md shadow-sm">
        <h4 class="text-sm font-medium text-gray-700 mb-1">Peak Hours</h4>
        <p class="text-xs text-gray-600">{{ formatPeakHours(popularityData.peak_hours) }}</p>
      </div>

      <!-- Busiest Days -->
      <div v-if="popularityData.busiest_days && popularityData.busiest_days.length > 0" class="mt-3 p-3 bg-gray-50 rounded-md shadow-sm">
        <h4 class="text-sm font-medium text-gray-700 mb-1">Busiest Days</h4>
        <ChipListDisplay :items="popularityData.busiest_days" emptyText="N/A" />
      </div>
      
      <!-- Traffic Patterns (Weekend/Weekday) -->
      <div v-if="popularityData.weekend_traffic || popularityData.weekday_traffic" class="mt-3 p-3 bg-gray-50 rounded-md shadow-sm">
        <h4 class="text-sm font-medium text-gray-700 mb-1">Traffic Patterns</h4>
        <p v-if="popularityData.weekday_traffic" class="text-xs text-gray-600">Weekday: {{ popularityData.weekday_traffic }}</p>
        <p v-if="popularityData.weekend_traffic" class="text-xs text-gray-600">Weekend: {{ popularityData.weekend_traffic }}</p>
      </div>

      <!-- Other generic popularity data points -->
      <div v-for="(value, key) in otherPopularityData" :key="key" class="mt-3 p-3 bg-gray-50 rounded-md shadow-sm">
         <h4 class="text-sm font-medium text-gray-700 mb-1">{{ formatKey(key) }}</h4>
         <p class="text-xs text-gray-600">{{ value }}</p>
      </div>

    </div>
    <div v-else class="text-sm text-gray-500 italic">
      {{ emptyText }}
    </div>
  </div>
</template>

<script>
import ChipListDisplay from './ChipListDisplay.vue';

export default {
  name: 'PopularityDisplay',
  components: { ChipListDisplay },
  props: {
    popularityData: { // Expects a complex object with various popularity metrics
      type: Object,
      default: () => ({})
    },
    title: {
      type: String,
      default: 'Popularity Insights'
    },
    emptyText: {
      type: String,
      default: 'Popularity data not available'
    }
  },
  computed: {
    otherPopularityData() {
      if (!this.popularityData) return {};
      const knownKeys = ['popular_times', 'histogram', 'peak_hours', 'busiest_days', 'weekend_traffic', 'weekday_traffic'];
      return Object.fromEntries(
        Object.entries(this.popularityData).filter(([key]) => !knownKeys.includes(key))
      );
    }
  },
  methods: {
    formatKey(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    getDayName(dayIndex) {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      // Google often uses 0 for Sunday or Monday depending on context, GMB popular times usually starts Monday=0
      // For generic popular_times, it might be 0=Monday, 1=Tuesday, ..., 6=Sunday
      // Or from Google Places API it could be Sunday=0. This needs to be flexible or specific to data source.
      // Assuming dayIndex is 0-6 where 0 might be Monday for some datasets, Sunday for others.
      // A more robust solution would infer this from the data source if possible.
      const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      return dayNames[dayIndex] || `Day ${dayIndex + 1}`;
    },
    getHourLabel(hourIndex) {
      // Assuming hourIndex is 0-23
      const hour = hourIndex % 24;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12; // Convert 0 to 12 for 12 AM
      return `${formattedHour} ${ampm}`;
    },
    formatPeakHours(peakHours) {
      if (Array.isArray(peakHours)) {
        return peakHours.join(', ');
      }
      return peakHours?.toString() || 'N/A';
    }
  }
};
</script>

<style scoped>
.tooltip-container {
  position: relative;
  display: inline-block;
}

.tooltip-text {
  visibility: hidden;
  width: 120px;
  background-color: #555;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 5px 0;
  position: absolute;
  z-index: 1;
  bottom: 125%; /* Position the tooltip above the item */
  left: 50%;
  margin-left: -60px; /* Use half of the width to center the tooltip */
  opacity: 0;
  transition: opacity 0.3s;
}

.tooltip-container:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}
</style> 